import { useEffect, useState } from 'react';
import { REPORT_STATUS, ROLE } from '../../../lib/constants';

/**
 * 報告書一覧におけるチェックボックスの状態を管理するカスタムフック。
 *
 * @param {Object[]} reports - 表示する報告書の配列。
 * @param {Function} onSelect - 個別の報告書のチェックボックスが変更されたときに呼び出される関数。
 * @param {Function} onSelectAll - すべて選択するチェックボックスが変更されたときに呼び出される関数。
 * @param {string} currentUserRole - 現在のユーザーの権限。
 * @param {Object} currentUserInfo - 現在のユーザーの情報。
 * @param {number} currentPage - 現在のページ番号。
 * @returns {Object} チェックボックスの状態とイベントハンドラを含むオブジェクト。
 */
const useCheckboxState = (
  reports,
  onSelect,
  onSelectAll,
  currentUserRole,
  currentUserInfo,
  currentPage,
) => {
  const [allChecked, setAllChecked] = useState(false);
  const [checkedItems, setCheckedItems] = useState({});

  // 「すべて選択」チェックボックスが変更されたときに呼び出される関数
  const handleAllChecked = event => {
    const isChecked = event.target.checked;
    setAllChecked(isChecked);
    const items = {};

    reports.forEach(report => {
      const shouldDisplayCheckbox =
        currentUserRole === ROLE.COMPANY_ADMIN ||
        currentUserRole === ROLE.ADMIN ||
        (currentUserRole === ROLE.GENERAL &&
          currentUserInfo.operatorId === report.createdOperatorId) ||
        (currentUserRole === ROLE.GUEST &&
          report.status !== REPORT_STATUS.COMPLETE);

      // ステータスが下書きかつユーザーIDが異なる場合、チェック不可
      const isCreateStatusButDifferentUser =
        report.status === REPORT_STATUS.CREATE &&
        currentUserInfo.operatorId !== report.createdOperatorId;

      // 上記条件を満たす場合は、チェック項目から除外
      if (!isCreateStatusButDifferentUser) {
        items[report.id] = isChecked && shouldDisplayCheckbox;
      }
    });

    setCheckedItems(items);
    onSelectAll();
  };

  // 個別のチェックボックスが変更されたときに呼び出される関数
  const handleCheckChange = (event, reportId) => {
    event.stopPropagation();
    const isChecked = event.target.checked;

    setCheckedItems(prevState => {
      const updatedCheckedItems = { ...prevState, [reportId]: isChecked };
      // 全てのチェックボックスが選択されているかチェック
      const allItemsChecked = reports.every(report =>
        updatedCheckedItems[report.id] !== undefined
          ? updatedCheckedItems[report.id]
          : false,
      );
      // 全選択状態を更新
      setAllChecked(allItemsChecked);
      return updatedCheckedItems;
    });

    onSelect(reportId);
  };

  useEffect(() => {
    setAllChecked(false);
    setCheckedItems({});
  }, [currentPage]);

  return { allChecked, checkedItems, handleAllChecked, handleCheckChange };
};

export default useCheckboxState;
